import React, { lazy, Suspense } from "react";
import { ConfigProvider } from "./COREDesignSystem/Layout/ConfigProvider";
import { message } from "antd";
import {
  createBrowserRouter,
  RouteProps,
  RouterProvider,
  useNavigate,
} from "react-router-dom";
import "./App.less";
// import "leaflet/dist/leaflet.css";
/** Layouts **/
import { LoginLayoutRoute } from "./modules/pages/layouts/LoginLayoutRoute";
import { CORELoading } from "./COREDesignSystem/Feedback/CORELoading";
import { useNetworkState, usePrevious } from "react-use";
import { PageLayoutRoute } from "./modules/pages/layouts/PageLayoutRoute";
import { DEFAULT_HOMEPAGE_URL } from "./shared/global";
import { QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { appQueryClient } from "./shared/state/appQueryClient";
import { datum } from "./shared/datum";
import { PageLayout } from "./modules/pages/layouts/PageLayout";
import { useUserLoggedInStatus } from "./shared/state/user";
import { Helmet } from "react-helmet";
import { isDevEnv } from "./shared/state/rest";
import { useRefreshToken } from "./shared/customHoooks/useRefreshToken";
import { ThemeProvider } from "./theme/ThemeProvider";
import { useReactQuerySubscription } from "./shared/state/websocket";
import { RecordUserVisitPage } from "./modules/pages/RecordUserVisitPage";

type RouteList = {
  path: Exclude<RouteProps["path"], undefined | readonly string[]>;
  component:
    | React.LazyExoticComponent<React.FunctionComponent>
    | React.LazyExoticComponent<
        (props: { datum?: typeof datum }) => JSX.Element
      >;
};

const HomePage = lazy(() => import("./modules/pages/Home"));
const DefaultHomePage = lazy(
  () => import("./modules/globalHomePage/GlobalHomepagePageWrapper")
);
const LoginPage = lazy(() => import("./modules/pages/login/LoginPage"));
const LogoutPage = lazy(() => import("./modules/pages/logout/Logout"));
const NoMatchPage = lazy(
  () => import("./modules/pages/noMatchPage/NoMatchPage")
);
const SetPasswordPage = lazy(
  () => import("./modules/pages/resetPassword/SetPasswordPage")
);
const ActivateAccountPage = lazy(
  () => import("./modules/pages/activateAccount/ActivateAccountPage")
);
const ForgotPasswordPage = lazy(
  () => import("./modules/pages/forgotPassword/ForgotPasswordPage")
);
const VerifyTwoFactor = lazy(
  () => import("./modules/pages/verifyTwoFactor/VerifyTwoFactor")
);
const MFASetup = lazy(() => import("./modules/admin/userManagement/MFASetup"));
const MarketSchedule = lazy(
  () => import("./modules/marketsAdmin/MarketSchedulePageWrapper")
);
const PowerLiveMarketScreen = lazy(
  () => import("./modules/marketsAdmin/marketPrices/Power")
);
const CarbonLiveMarket = lazy(
  () => import("./modules/marketsAdmin/marketPrices/Carbon")
);
const EnviroLiveMarketScreen = lazy(
  () => import("./modules/marketsAdmin/marketPrices/Environmental")
);
const MarketsSnapshotPage = lazy(
  () => import("./modules/markets/MarketsSnapshotPageWrapper")
);

const RegistryViewerPage = lazy(
  () => import("./modules/registryViewer/RegistryViewerPageWrapper")
);
const MarketPricesPowerRedirect = lazy(
  () => import("./modules/marketsAdmin/marketPrices/MarketPricesPowerRedirect")
);
const DashboardPage = lazy(
  () => import("./modules/dashboards/DashboardPageWrapper")
);
const TradeTrackerPage = lazy(
  () => import("./modules/marketsAdmin/TradeTrackerPageWrapper")
);
const TradeTimelinePage = lazy(
  () => import("./modules/markets/TradeTimelinePageWrapper")
);
const UserAnalyticPage = lazy(
  () => import("./modules/admin/AnalyticPageWrapper")
);
const EventDetails = lazy(
  () =>
    import("./modules/admin/analytics/events/details/EventDetailsPageWrapper")
);
const UserManagementPage = lazy(
  () => import("./modules/admin/UserManagementPageWrapper")
);
const APITokensPage = lazy(() => import("./modules/admin/ApiTokenPageWrapper"));
const AddEditUserPageWrapper = lazy(
  () =>
    import("./modules/admin/userManagement/addEditUsers/AddEditUserPageWrapper")
);
const TodoPageWrapper = lazy(
  () => import("./modules/toDoList/TodoPageWrapper")
);
const MyDetailsPageWrapper = lazy(
  () =>
    import(
      "./modules/admin/userManagement/addEditUsers/myDetails/MyDetailsPageWrapper"
    )
);
const NotificationListPageWrapper = lazy(
  () => import("./modules/notifications/NotificationListPageWrapper")
);
const TraderViewPage = lazy(
  () => import("./modules/tools/TraderViewPageWrapper")
);

const DailyCurvePage = lazy(
  () => import("./modules/markets/DailyCurvePageWrapper")
);

const CarbonBenchmarksRedirect = lazy(
  () => import("./modules/markets/dailyCurves/CarbonBenchmarksRedirect")
);
const EnvironmentalBenchmarksRedirect = lazy(
  () => import("./modules/markets/dailyCurves/EnvironmentalBenchmarksRedirect")
);
const BenchmarksSectionRedirect = lazy(
  () => import("./modules/markets/dailyCurves/BenchmarksSectionRedirect")
);
const PowerBenchmarksRedirect = lazy(
  () => import("./modules/markets/dailyCurves/PowerBenchmarksRedirect")
);

const ChartBuilderPage = lazy(
  () => import("./modules/chartBuilder/ChartBuilderPageWrapper")
);
const CompanyPage = lazy(
  () => import("./modules/admin/CompanyManagementPageWrapper")
);
const AddCompanyPage = lazy(
  () =>
    import(
      "./modules/admin/companyManagement/addEditCompany/AddCompanyPageWrapper"
    )
);
const EditCompanyPage = lazy(
  () =>
    import(
      "./modules/admin/companyManagement/addEditCompany/EditCompanyPageWrapper"
    )
);

const SubscriptionPackagesPage = lazy(
  () => import("./modules/admin/SubscriptionPackagesPageWrapper")
);

const AddEditSubscriptionPackagesPage = lazy(
  () =>
    import("./modules/admin/subscriptionPackage/AddEditSubscriptionPackages")
);

const ProjectMarketplace = lazy(
  () =>
    import("./modules/projectMarketplaceLegacy/ProjectMarketplacePageWrapper")
);

const ProjectExplorer = lazy(
  () => import("./modules/projectMarketplace/ProjectExplorerPageWrapper")
);

const PowerAssetInformationPageWrapper = lazy(
  () =>
    import(
      "./modules/projectMarketplace/projectExplorer/projectInformation/PowerAssetInformationPageWrapper"
    )
);

const PowerProjectRegistryPageWrapper = lazy(
  () =>
    import(
      "./modules/projectMarketplace/projectExplorer/projectRegistry/PowerProjectRegistryPageWrapper"
    )
);

const PowerProjectGenerationPageWrapper = lazy(
  () =>
    import(
      "./modules/projectMarketplace/projectExplorer/projectGeneration/PowerProjectGenerationPageWrapper"
    )
);

const ProjectAnalysis = lazy(
  () => import("./modules/projectMarketplace/ProjectAnalysisPageWrapper")
);

const ProjectPortfoliosOverviewPage = lazy(
  () =>
    import(
      "./modules/projectMarketplace/portfolios/ProjectPortfoliosOverviewPageWrapper"
    )
);

const ProjectPortfoliosEditPage = lazy(
  () =>
    import(
      "./modules/projectMarketplace/portfolios/ProjectPortfoliosEditPageWrapper"
    )
);

const AdminCentreReportsDataFixes = lazy(
  () =>
    import(
      "./modules/projectMarketplace/adminCentre/AdminCentreReportsDataFixesPageWrapper"
    )
);

const AdminCentreChangeLog = lazy(
  () =>
    import(
      "./modules/projectMarketplace/adminCentre/AdminCentreChangeLogPageWrapper"
    )
);

const AdminCentreOrganisations = lazy(
  () =>
    import(
      "./modules/projectMarketplace/adminCentre/AdminCentreOrganisationsPageWrapper"
    )
);

const AdminCentreSchemesAndTenders = lazy(
  () =>
    import(
      "./modules/projectMarketplace/adminCentre/AdminCentreSchemesAndTendersPageWrapper"
    )
);

const AdminCentreMomentumScoreFramework = lazy(
  () =>
    import(
      "./modules/projectMarketplace/adminCentre/AdminCentreMomentumScoreFrameworkPageWrapper"
    )
);

const AdminCentreSupplementaryInformation = lazy(
  () =>
    import(
      "./modules/projectMarketplace/adminCentre/AdminCentreSupplementaryInformationPageWrapper"
    )
);

const AdminCentreAddEditAsset = lazy(
  () =>
    import(
      "./modules/projectMarketplace/adminCentre/AdminCentreAddEditAssetPageWrapper"
    )
);

const MarketplaceInsightsPowerProjectsPageWrapper = lazy(
  () =>
    import(
      "./modules/projectMarketplace/marketplaceInsights/PowerProjectInsightsPageWrapper"
    )
);

const MarketplaceInsightsOfftakeTrackerPageWrapper = lazy(
  () =>
    import(
      "./modules/projectMarketplace/marketplaceInsights/OfftakeTrackerPageWrapper"
    )
);

const PlanningTimelinePageWrapper = lazy(
  () =>
    import(
      "./modules/projectMarketplace/planningTimeline/PlanningTimelinePageWrapper"
    )
);

const MarketplaceInsightsOfftakeBenchmarksCommentaryPageWrapper = lazy(
  () =>
    import(
      "./modules/projectMarketplace/marketplaceInsights/OfftakeBenchmarksCommentaryPageWrapper"
    )
);

const AssetIntelligenceMarketDynamicsRedirect = lazy(
  () =>
    import(
      "./modules/projectMarketplace/marketplaceInsights/AssetIntelligenceMarketDynamicsRedirect"
    )
);

const DBActivity = lazy(() => import("./modules/superAdmin/DBActivity"));
const ProjectDetail = lazy(
  () =>
    import(
      "./modules/projectMarketplaceLegacy/projectMarketplaceDetail/ProjectMarketPlaceDetail"
    )
);
const CertificateProjectPageWrapper = lazy(
  () => import("./modules/marketsAdmin/CertificateProjectPageWrapper")
);
const CertificateProjectDetails = lazy(
  () =>
    import(
      "./modules/marketsAdmin/certificateProjects/manageProjects/addEditProjects/CertificateProjectDetails"
    )
);

const SectionPage = lazy(() => import("./modules/section/Index"));

const EmissionsCarbonBudgets = lazy(
  () => import("./modules/emissions/CarbonBudgets")
);

const EmissionsAddInitiatives = lazy(
  () => import("./modules/emissions/initiativeComponents/AddInitiatives")
);

const EmissionsViewInitiatives = lazy(
  () => import("./modules/emissions/ViewInitiatives")
);

const EmissionsSpecificPortfolioInitiatives = lazy(
  () =>
    import(
      "./modules/emissions/initiativeComponents/SpecificPortfolioInitiatives"
    )
);
const EmissionsScenarios = lazy(
  () => import("./modules/emissions/ScenariosPage")
);
const EmissionsScenario = lazy(
  () => import("./modules/emissions/scenarioComponents/ScenarioPage")
);

const EmissionsViewOffsets = lazy(
  () => import("./modules/emissions/ViewOffsets")
);

const EmissionsAddOffsets = lazy(
  () => import("./modules/emissions/offsetComponents/AddOffsets")
);

const EmissionsSpecificPortfolioOffsets = lazy(
  () => import("./modules/emissions/offsetComponents/SpecificPortfolioOffsets")
);

const EmissionsPortfolios = lazy(
  () => import("./modules/emissions/Portfolios")
);

const EmissionsAddPortfolios = lazy(
  () =>
    import(
      "./modules/emissions/portfolioComponents/createAndEdit/CreateEditPortfolio"
    )
);

const EmissionsEditPortfoliosPreferences = lazy(
  () =>
    import(
      "./modules/emissions/portfolioComponents/portfolioPreferences/EditPortfoliosPreferences"
    )
);

const EmissionsReporting = lazy(() => import("./modules/emissions/Reporting"));

const EmissionsGettingStarted = lazy(
  () => import("./modules/emissions/GettingStartedEmissions")
);

const EmissionsOverview = lazy(() => import("./modules/emissions/Overview"));

const EmissionPortfolioDetail = lazy(
  () =>
    import(
      "./modules/emissions/portfolioComponents/portfolioDetail/PortfolioDetail"
    )
);

const MarketsAdminCounterParties = lazy(
  () => import("./modules/marketsAdmin/CounterpartyPageWrapper")
);

const MarketsAdminAwaybrokers = lazy(
  () => import("./modules/marketsAdmin/AwaybrokerPageWrapper")
);
const AddCounterParties = lazy(
  () =>
    import(
      "./modules/marketsAdmin/tradeParties/counterParties/addEditCounterParties/profile/AddProfileContainer"
    )
);
const EditCounterParties = lazy(
  () =>
    import(
      "./modules/marketsAdmin/tradeParties/counterParties/addEditCounterParties/profile/EditProfileContainer"
    )
);
const Reports = lazy(() => import("./modules/insights/ReportsPageWrapper"));

const ReportsDetail = lazy(
  () => import("./modules/insights/reports/ReportsDetail")
);

const CMSContentManagementPublicationsPage = lazy(
  () =>
    import(
      "./modules/admin/contentManagement/publications/PublicationsPageWrapper"
    )
);
const CMSContentManagementPublicationsAddPage = lazy(
  () => import("./modules/admin/contentManagement/publications/AddPublications")
);
const CMSContentManagementPublicationsEditPage = lazy(
  () =>
    import("./modules/admin/contentManagement/publications/EditPublications")
);
const CMSContentManagementNewsAndAlertPage = lazy(
  () =>
    import(
      "./modules/admin/contentManagement/newsAndAlerts/NewsAndAlertsPageWrapper"
    )
);
const CMSContentManagementAddNewsAndAlertPage = lazy(
  () =>
    import(
      "./modules/admin/contentManagement/newsAndAlerts/AddEditNewsAndAlertsPageWrapper"
    )
);
const InsightNewsAndAlertsPage = lazy(
  () => import("./modules/insights/InsightNewsAndAlertsPageWrapper")
);

const ArticlesAndUpdatesPage = lazy(
  () => import("./modules/insights/ArticlesAndUpdatesPageWrapper")
);

const ArticlesPage = lazy(
  () => import("./modules/insights/ArticlesPageWrapper")
);

const MarketUPdatePage = lazy(
  () => import("./modules/insights/MarketUpdatePageWrapper")
);
const MarketCarbonEnvironmentalPowerPageWrapper = lazy(
  () => import("./modules/markets/MarketCarbonEnvironmentalPowerPageWrapper")
);
const NotificationSettingsPageWrapper = lazy(
  () =>
    import(
      "./modules/admin/userManagement/addEditUsers/NotificationSettingsPageWrapper"
    )
);
const PreferencesPageWrapper = lazy(
  () =>
    import(
      "./modules/admin/userManagement/addEditUsers/preferences/PreferencesPageWrapper"
    )
);

const chartBuilderRouteList: RouteList[] = [
  {
    path: "/chart-builder",
    component: ChartBuilderPage,
  },
];
const TransactionHistoryPage = lazy(
  () => import("./modules/account/myCompany/TransactionHistoryPageWrapper")
);

const toolsRouteList: RouteList[] = [
  {
    path: "/tools/trader-view",
    component: TraderViewPage,
  },
  {
    path: "/tools",
    component: SectionPage,
  },
];

const marketsAdminRouteList: RouteList[] = [
  {
    path: "/markets-admin/market-prices/power",
    component: PowerLiveMarketScreen,
  },
  {
    path: "/markets-admin/market-prices/electricity-shapes",
    component: MarketPricesPowerRedirect,
  },
  {
    path: "/markets-admin/market-prices/electricity",
    component: MarketPricesPowerRedirect,
  },
  {
    path: "/markets-admin/market-prices/carbon",
    component: CarbonLiveMarket,
  },
  {
    path: "/markets-admin/market-prices/environmental",
    component: EnviroLiveMarketScreen,
  },
  {
    path: "/markets-admin/market-prices",
    component: SectionPage,
  },
  {
    path: "/market-prices",
    component: SectionPage,
  },
  {
    path: "/markets-admin/certificate-projects/projects/add",
    component: CertificateProjectDetails,
  },
  {
    path: "/markets-admin/certificate-projects/projects/:id/:menuName",
    component: CertificateProjectDetails,
  },
  {
    path: "/markets-admin/certificate-projects/:menuName",
    component: CertificateProjectPageWrapper,
  },
  {
    path: "/markets-admin/certificate-projects",
    component: SectionPage,
  },
  {
    path: "/markets-admin/trade-parties/away-brokers",
    component: MarketsAdminAwaybrokers,
  },
  {
    path: "/markets-admin/trade-parties/counterparties/:id/profile",
    component: EditCounterParties,
  },
  {
    path: "/markets-admin/trade-parties/counterparties/add",
    component: AddCounterParties,
  },
  {
    path: "/markets-admin/trade-parties/counterparties",
    component: MarketsAdminCounterParties,
  },
  {
    path: "/markets-admin/trade-parties",
    component: SectionPage,
  },
  {
    path: "/markets-admin/trade-tracker",
    component: TradeTrackerPage,
  },
  {
    path: "/markets-admin/market-schedules/:menu",
    component: MarketSchedule,
  },
  {
    path: "/markets-admin/market-schedules",
    component: SectionPage,
  },
  {
    path: "/markets-admin",
    component: SectionPage,
  },
];

const registryViewerRouteList: RouteList[] = [
  {
    path: "/registry-viewer/:menu",
    component: RegistryViewerPage,
  },
  {
    path: "/registry-viewer",
    component: SectionPage,
  },
];

const marketsRouteList: RouteList[] = [
  {
    path: "/markets/daily-curves/carbon/:date?",
    component: CarbonBenchmarksRedirect,
  },
  {
    path: "/markets/daily-curves/environmental/:date?",
    component: EnvironmentalBenchmarksRedirect,
  },
  {
    path: "/markets/daily-curves/electricity/:date?",
    component: PowerBenchmarksRedirect,
  },
  {
    path: "/markets/daily-curves/fixed-power-shapes/:date?",
    component: PowerBenchmarksRedirect,
  },
  {
    path: "/markets/daily-curves/hourly-power-shapes/:date?",
    component: PowerBenchmarksRedirect,
  },
  {
    path: "/markets/daily-curves/:productClass/:date?",
    component: DailyCurvePage,
  },
  {
    path: "/markets/daily-curves",
    component: BenchmarksSectionRedirect,
  },
  {
    path: "/markets/benchmarks/:productClass/:date?",
    component: DailyCurvePage,
  },
  {
    path: "/markets/benchmarks",
    component: SectionPage,
  },
  {
    path: "/markets/carbon/australia",
    component: SectionPage,
  },
  {
    path: "/markets/:marketType/:region/:menuName",
    component: MarketCarbonEnvironmentalPowerPageWrapper,
  },
  {
    path: "/markets/carbon/international",
    component: SectionPage,
  },
  {
    path: "/markets/carbon/new-zealand",
    component: SectionPage,
  },
  {
    path: "/markets/carbon",
    component: SectionPage,
  },
  {
    path: "/markets/environmental/australia",
    component: SectionPage,
  },
  {
    path: "/markets/environmental",
    component: SectionPage,
  },
  {
    path: "/markets/power/australia",
    component: SectionPage,
  },
  {
    path: "/markets/power",
    component: SectionPage,
  },
  {
    path: "/markets/markets-snapshot",
    component: MarketsSnapshotPage,
  },
  {
    path: "/markets/trade-timeline",
    component: TradeTimelinePage,
  },
  {
    path: "/markets",
    component: SectionPage,
  },
];

const projectMarketplaceRouteList: RouteList[] = [
  {
    path: "/markets/carbon-projects/details/:id",
    component: ProjectDetail,
  },
  {
    path: "/markets/carbon-projects",
    component: ProjectMarketplace,
  },
  {
    path: "/asset-intelligence/admin-centre/data-fixes",
    component: AdminCentreReportsDataFixes,
  },
  {
    path: "/asset-intelligence/admin-centre/change-log",
    component: AdminCentreChangeLog,
  },
  {
    path: "/asset-intelligence/admin-centre/organisations",
    component: AdminCentreOrganisations,
  },
  {
    path: "/asset-intelligence/admin-centre/schemes-and-tenders",
    component: AdminCentreSchemesAndTenders,
  },
  {
    path: "/asset-intelligence/admin-centre/momentum-score-framework",
    component: AdminCentreMomentumScoreFramework,
  },
  {
    path: "/asset-intelligence/admin-centre/supplementary-information",
    component: AdminCentreSupplementaryInformation,
  },
  {
    path: "/asset-intelligence/admin-centre/add-asset",
    component: AdminCentreAddEditAsset,
  },
  {
    path: "/asset-intelligence/admin-centre/:id/edit",
    component: AdminCentreAddEditAsset,
  },
  {
    path: "/asset-intelligence/admin-centre",
    component: SectionPage,
  },
  {
    path: "/asset-intelligence",
    component: SectionPage,
  },
  {
    path: "/asset-intelligence/explorer/:id/asset-information",
    component: PowerAssetInformationPageWrapper,
  },
  {
    path: "/asset-intelligence/explorer/:id/registry-insights",
    component: PowerProjectRegistryPageWrapper,
  },
  {
    path: "/asset-intelligence/explorer/:id/generation",
    component: PowerProjectGenerationPageWrapper,
  },
  {
    path: "/asset-intelligence/explorer",
    component: ProjectExplorer,
  },
  {
    path: "/asset-intelligence/scenario-analysis",
    component: ProjectAnalysis,
  },
  {
    path: "/asset-intelligence/portfolios",
    component: ProjectPortfoliosOverviewPage,
  },
  {
    path: "/asset-intelligence/portfolios/:id/edit",
    component: ProjectPortfoliosEditPage,
  },
  {
    path: "/asset-intelligence/offtake-insights",
    component: AssetIntelligenceMarketDynamicsRedirect,
  },
  {
    path: "/asset-intelligence/market-tracker",
    component: MarketplaceInsightsPowerProjectsPageWrapper,
  },
  {
    path: "/asset-intelligence/offtake-tracker",
    component: MarketplaceInsightsOfftakeTrackerPageWrapper,
  },
  {
    path: "/asset-intelligence/planning-timeline",
    component: PlanningTimelinePageWrapper,
  },
  {
    path: "/asset-intelligence/market-dynamics",
    component: MarketplaceInsightsOfftakeBenchmarksCommentaryPageWrapper,
  },
  {
    path: "/asset-intelligence/benchmarks-commentary",
    component: AssetIntelligenceMarketDynamicsRedirect,
  },
];

const adminRouteList: RouteList[] = [
  {
    path: "/admin/user-management/add",
    component: AddEditUserPageWrapper,
  },
  {
    path: "/admin/api-tokens",
    component: APITokensPage,
  },
  {
    path: "/admin/user-management/:id/profile",
    component: AddEditUserPageWrapper,
  },
  {
    path: "/admin/user-management/:id/preferences",
    component: PreferencesPageWrapper,
  },
  {
    path: "/admin/user-management/:id/notifications",
    component: NotificationSettingsPageWrapper,
  },
  {
    path: "/admin/user-management",
    component: UserManagementPage,
  },
  {
    path: `/admin/analytics/events/:id/details`,
    component: EventDetails,
  },
  {
    path: `/admin/analytics/:pageName`,
    component: UserAnalyticPage,
  },
  {
    path: "/admin/analytics",
    component: SectionPage,
  },
  {
    path: "/admin/subscription-packages/add",
    component: AddEditSubscriptionPackagesPage,
  },
  {
    path: "/admin/subscription-packages/:id",
    component: AddEditSubscriptionPackagesPage,
  },
  {
    path: "/admin/content-management/publications/:id/edit",
    component: CMSContentManagementPublicationsEditPage,
  },
  {
    path: "/admin/content-management/publications/add",
    component: CMSContentManagementPublicationsAddPage,
  },
  {
    path: "/admin/content-management/publications",
    component: CMSContentManagementPublicationsPage,
  },
  {
    path: "/admin/content-management",
    component: SectionPage,
  },
  {
    path: "/admin/subscription-packages",
    component: SubscriptionPackagesPage,
  },
  {
    path: "/admin/company-management/:id/overview",
    component: EditCompanyPage,
  },
  {
    path: "/admin/company-management/add",
    component: AddCompanyPage,
  },
  {
    path: "/admin/company-management",
    component: CompanyPage,
  },
  {
    path: "/admin/content-management/news-and-alerts/add",
    component: CMSContentManagementAddNewsAndAlertPage,
  },
  {
    path: "/admin/content-management/news-and-alerts/:id/edit",
    component: CMSContentManagementAddNewsAndAlertPage,
  },
  {
    path: "/admin/content-management/news-and-alerts",
    component: CMSContentManagementNewsAndAlertPage,
  },
  {
    path: "/admin/db-activity",
    component: DBActivity,
  },
  {
    path: "/admin",
    component: SectionPage,
  },
];

const emissionsManagerRouteList: RouteList[] = [
  {
    path: `/emissions-manager/getting-started`,
    component: EmissionsGettingStarted,
  },
  {
    path: `/emissions-manager/carbon-budgets`,
    component: EmissionsCarbonBudgets,
  },
  {
    path: `/emissions-manager/initiatives/add`,
    component: EmissionsAddInitiatives,
  },
  {
    path: `/emissions-manager/initiatives/:id/view`,
    component: EmissionsViewInitiatives,
  },
  {
    path: `/emissions-manager/initiatives/:id`,
    component: EmissionsSpecificPortfolioInitiatives,
  },
  {
    path: `/emissions-manager/initiatives`,
    component: EmissionsSpecificPortfolioInitiatives,
  },
  {
    path: `/emissions-manager/scenarios/:id`,
    component: EmissionsScenario,
  },
  {
    path: `/emissions-manager/scenarios`,
    component: EmissionsScenarios,
  },

  {
    path: `/emissions-manager/offsets/add`,
    component: EmissionsAddOffsets,
  },
  {
    path: `/emissions-manager/offsets/:id/view`,
    component: EmissionsViewOffsets,
  },
  {
    path: `/emissions-manager/offsets/:id`,
    component: EmissionsSpecificPortfolioOffsets,
  },
  {
    path: `/emissions-manager/offsets`,
    component: EmissionsSpecificPortfolioOffsets,
  },
  {
    path: `/emissions-manager/portfolios/create`,
    component: EmissionsAddPortfolios,
  },
  {
    path: `/emissions-manager/portfolios/:id/details/:scopeId/:page`,
    component: EmissionPortfolioDetail,
  },
  {
    path: `/emissions-manager/portfolios/:id/preferences/:page`,
    component: EmissionsEditPortfoliosPreferences,
  },
  {
    path: `/emissions-manager/portfolios/:id/edit`,
    component: EmissionsAddPortfolios,
  },
  {
    path: `/emissions-manager/portfolios`,
    component: EmissionsPortfolios,
  },
  {
    path: `/emissions-manager/reporting`,
    component: EmissionsReporting,
  },
  {
    path: `/emissions-manager/overview/:id`,
    component: EmissionsOverview,
  },
  {
    path: `/emissions-manager/overview`,
    component: EmissionsOverview,
  },
  {
    path: `/emissions-manager`,
    component: SectionPage,
  },
];

const otherRouteList: RouteList[] = [
  {
    path: "/dashboard/:name",
    component: DashboardPage,
  },
  {
    path: "/",
    component: HomePage,
  },
  {
    path: "/dashboards",
    component: SectionPage,
  },
  {
    path: DEFAULT_HOMEPAGE_URL,
    component: DefaultHomePage,
  },
];

const insightsRouteList: RouteList[] = [
  {
    path: "/insights/reports/all-reports/:id/view",
    component: ReportsDetail,
  },
  {
    path: "/insights/reports/carbon-enviro/:id/view",
    component: ReportsDetail,
  },
  {
    path: "/insights/reports/market-forecasts/:id/view",
    component: ReportsDetail,
  },
  {
    path: "/insights/reports/power-reports/:id/view",
    component: ReportsDetail,
  },
  {
    path: "/insights/reports/research-reports/:id/view",
    component: ReportsDetail,
  },
  {
    path: "/insights/reports/bespoke-reports/:id/view",
    component: ReportsDetail,
  },
  {
    path: "/insights/reports/:menu",
    component: Reports,
  },
  {
    path: "/insights/reports",
    component: SectionPage,
  },
  {
    path: "/insights/market-updates/:id/view",
    component: ReportsDetail,
  },
  {
    path: "/insights/articles-and-updates",
    component: ArticlesAndUpdatesPage,
  },
  {
    path: "/insights/articles/:id/view",
    component: ReportsDetail,
  },
  {
    path: "/insights/articles",
    component: ArticlesPage,
  },
  {
    path: "/insights/market-updates",
    component: MarketUPdatePage,
  },
  {
    path: "/insights",
    component: SectionPage,
  },
];

const loginRouteList: RouteList[] = [
  {
    path: "/login",
    component: LoginPage,
  },
  {
    path: "/set-password/:token",
    component: SetPasswordPage,
  },
  {
    path: "/account-activation",
    component: ActivateAccountPage,
  },
  {
    path: "/forgot-password",
    component: ForgotPasswordPage,
  },
  {
    path: "/verify-two-factor/:userToken",
    component: VerifyTwoFactor,
  },
];

const insightRouteList: RouteList[] = [
  {
    path: "/insights/news-and-alerts",
    component: InsightNewsAndAlertsPage,
  },
  {
    path: "/insights",
    component: SectionPage,
  },
];

const accountRouteList: RouteList[] = [
  {
    path: "/account",
    component: SectionPage,
  },
  {
    path: "/account/user",
    component: SectionPage,
  },
  {
    path: "/account/user/profile",
    component: MyDetailsPageWrapper,
  },
  {
    path: "/account/user/preferences",
    component: PreferencesPageWrapper,
  },
  {
    path: "/account/user/notifications",
    component: NotificationSettingsPageWrapper,
  },
  {
    path: "/account/company",
    component: SectionPage,
  },
  {
    path: "/account/company/transaction-history",
    component: TransactionHistoryPage,
  },
  {
    path: "/todo",
    component: TodoPageWrapper,
  },
  {
    path: "/notifications",
    component: NotificationListPageWrapper,
  },
];

const allRouteListExceptLogin: RouteList[] = [
  ...chartBuilderRouteList,
  ...toolsRouteList,
  ...marketsAdminRouteList,
  ...marketsRouteList,
  ...adminRouteList,
  ...insightsRouteList,
  ...projectMarketplaceRouteList,
  ...emissionsManagerRouteList,
  ...registryViewerRouteList,
  ...insightRouteList,
  ...accountRouteList,
  ...otherRouteList,
];

function Websockets() {
  const navigate = useNavigate();
  const onNavigate = (url: string) => navigate(url);
  useReactQuerySubscription(onNavigate);
  return null;
}

const RefreshToken: React.FC = () => {
  useRefreshToken();
  return null;
};

const IsLoadingPage = () => {
  const isLoggedIn = useUserLoggedInStatus();
  return isLoggedIn ? (
    <PageLayout>
      <CORELoading message="Loading Page" delay={0} />
    </PageLayout>
  ) : (
    <CORELoading message="Loading Page" delay={0} />
  );
};

export const App = () => {
  const { online } = useNetworkState();
  const prevOnline = usePrevious(online);
  const onlineStatusChanged = prevOnline !== online && prevOnline !== undefined;

  if (onlineStatusChanged && online)
    message.info("Internet connection restored.");
  else if (onlineStatusChanged && online === false)
    message.warning("Lost internet connection.");

  const faviconPrefix = isDevEnv() ? "dev_" : "";

  return (
    <QueryClientProvider client={appQueryClient}>
      <ThemeProvider>
        <RouterProvider
          router={createBrowserRouter([
            {
              path: "/mfa-setup",
              element: (
                <Suspense fallback={<CORELoading delay={0} />}>
                  <MFASetup />
                </Suspense>
              ),
            },
            ...allRouteListExceptLogin.map(
              ({ path, component: LazyComponent }) => ({
                path,
                element: (
                  <ConfigProvider>
                    <PageLayoutRoute
                      path={path}
                      element={
                        <Suspense fallback={<IsLoadingPage />}>
                          <RecordUserVisitPage />
                          <Websockets />
                          <LazyComponent />
                        </Suspense>
                      }
                    />
                  </ConfigProvider>
                ),
              })
            ),
            ...loginRouteList.map(({ path, component: LazyComponent }) => ({
              path,
              element: (
                <LoginLayoutRoute
                  element={
                    <Suspense fallback={<IsLoadingPage />}>
                      <LazyComponent />
                    </Suspense>
                  }
                />
              ),
            })),
            {
              path: "/logout",
              element: (
                <Suspense fallback={<div>Loading Logout...</div>}>
                  <LogoutPage />
                </Suspense>
              ),
            },
            {
              path: "*",
              element: (
                <Suspense fallback={<div>Loading 404...</div>}>
                  <NoMatchPage />
                </Suspense>
              ),
            },
          ])}
        />

        <RefreshToken />
        <Helmet>
          <link
            rel="icon"
            type="image/x-icon"
            href={`/${faviconPrefix}favicon.ico`}
          />
          <link rel="shortcut icon" href={`/${faviconPrefix}favicon.ico`} />
          <link rel="manifest" href={`/${faviconPrefix}manifest.json`} />
          <link
            rel="apple-touch-icon"
            sizes="180x180"
            href={`/${faviconPrefix}apple-touch-icon.png`}
          />
        </Helmet>
        <ReactQueryDevtools initialIsOpen={false} />
      </ThemeProvider>
    </QueryClientProvider>
  );
};
